import { useEffect, useRef, useState } from 'react';

import { WizardStatusLayout } from './WizardStatusLayout';

type Phase = {
  label: string;
  description: string;
};

type Props = {
  subtitle: string;
  phases: Phase[];
  totalDurationMs?: number;
};

export const LoadingGeneratingRecommendations = ({
  subtitle,
  phases,
  totalDurationMs = 240000,
}: Props) => {
  const phaseDurationMs = totalDurationMs / phases.length;
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [activeProgress, setActiveProgress] = useState(0);
  const phaseStartRef = useRef(0);

  useEffect(() => {
    phaseStartRef.current = performance.now();

    const interval = setInterval(() => {
      const elapsed = performance.now() - phaseStartRef.current;
      const ratio = Math.min(1, elapsed / phaseDurationMs);
      const baseTarget = ratio * 100;
      const jitter = Math.random() * 3;
      setActiveProgress((prev) =>
        Math.min(100, Math.max(prev, baseTarget + jitter))
      );
    }, 300);

    return () => clearInterval(interval);
  }, [phaseIndex, phaseDurationMs]);

  useEffect(() => {
    if (phaseIndex >= phases.length - 1) {
      return;
    }
    const timer = setTimeout(() => {
      setPhaseIndex((prev) => prev + 1);
    }, phaseDurationMs);
    return () => clearTimeout(timer);
  }, [phaseIndex, phases.length, phaseDurationMs]);

  const currentPhase = phases[Math.min(phaseIndex, phases.length - 1)];
  const overallProgress = Math.round(
    ((phaseIndex + activeProgress / 100) / phases.length) * 100
  );

  return (
    <WizardStatusLayout
      variant="opsPilot2"
      title={`${overallProgress}% ready`}
      subtitle={subtitle}
      progress={overallProgress}
      progressDescription={currentPhase.description}
    />
  );
};
